import { ReactElement, FC, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { useAppSelector } from '~/redux/hooks'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import TimesIcon from '~/components/Icons/svgs/TimesIcon'
import LoaderSkeleton from '~/shared/components/LoaderSkeleton'
import { selectCurriculumSubjectContent } from '~/curriculum/slice/curriculum.slice'
import useLastSelectedGradeTitle from '~/curriculum/hooks/useGradeTitleFromLevel'

import ButtonOrange from './ActionButtonOrange'

interface MindDetailsOverlayProps {
  onDismiss: () => void
}

interface MindLearningItemProps {
  text: string
}

const MindLearningItem: FC<MindLearningItemProps> = ({ text }) => (
  <div className="w-full flex items-center gap-3 bg-white rounded px-4 py-3">
    <div className="flex-shrink-0">
      <ResponsiveImage height={185} imageClassName="w-11 -rotate-30" srcKey="landing/mind-ai-v2.png" width={185} />
    </div>
    <p className="font-medium text-xs md:text-base text-left text-black-light-fade flex-1">{text}</p>
  </div>
)

export default function MindDetailsOverlay({ onDismiss }: MindDetailsOverlayProps): ReactElement {
  const { t } = useTranslation(['curriculum', 'generics'])
  const subjectContent = useAppSelector(selectCurriculumSubjectContent)
  const router = useRouter()

  // Get grade title using the new hook
  const gradeTitle = useLastSelectedGradeTitle()

  // Prevent scrolling when overlay is open
  useEffect(() => {
    // Store original styles
    const originalStyle = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    }

    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

    // Apply scroll lock styles
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${window.scrollY}px`
    document.body.style.width = '100%'

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    // Store scroll position for restoration
    const scrollY = window.scrollY

    return () => {
      // Restore original styles
      document.body.style.overflow = originalStyle.overflow
      document.body.style.paddingRight = originalStyle.paddingRight
      document.body.style.position = originalStyle.position
      document.body.style.top = originalStyle.top
      document.body.style.width = originalStyle.width

      // Restore scroll position
      window.scrollTo(0, scrollY)
    }
  }, [])

  // Check if we're specifically on curriculum overview page (with subject parameter)
  const isCurriculumOverview =
    // router.pathname.includes('/curriculum') &&
    router.pathname.match(/\/curriculum\/[^/]+\/[^/]+\/[^/]+/) && // Matches /curriculum/[countryCode]/[grade]/[subject]
    !router.pathname.includes('mapping-quiz')

  // Use full screen for all pages except curriculum overview
  const containerClass = isCurriculumOverview ? 'fixed top-11 md:top-16 left-0 right-0 bottom-0' : 'fixed inset-0'

  return createPortal(
    <div
      className={`${containerClass} z-modal-chest md:z-modal-static flex flex-col items-center bg-purple-darker overflow-y-auto`}
    >
      <div className="flex w-full justify-end p-4 md:p-6 pt-10 absolute -top-3 md:top-0">
        <button className="" onClick={onDismiss}>
          <SVGIcon icon={TimesIcon} pathClassName="text-white" size={16} />
        </button>
      </div>
      <div className="flex flex-col max-w-96 h-full px-7 mt-4 lg:mt-6 items-center justify-center">
        <div className="flex flex-col items-center w-full flex-shrink-0">
          {/* Top Section with Brain Icon */}
          <ResponsiveImage
            height={185}
            imageClassName="w-30 md:w-46 -rotate-30"
            srcKey="landing/mind-ai-v2.png"
            width={185}
          />
          <h2 className="text-white self-start text-xs md:text-base mt-8 mb-3">
            {t('curriculum:onboarding.latestLearning')}
          </h2>

          {/* Learning Items */}
          <div className="space-y-3 w-full">
            {subjectContent?.subjectLabel && (
              <MindLearningItem
                text={t('curriculum:onboarding.unlockedMasteryTrack', { subject: subjectContent.subjectLabel })}
              />
            )}
            {!gradeTitle && <LoaderSkeleton className="w-full rounded h-12" rows={1} />}
            {gradeTitle && <MindLearningItem text={t('curriculum:onboarding.youAreWorkingInGrade', { gradeTitle })} />}
          </div>

          {/* Divider */}
          <div className="w-9/10 border-t border-white my-8" />
        </div>

        {/* OK Button - centered in remaining space */}
        <div className="flex items-center justify-center w-104 max-w-screen-8/10 pt-4 pb-6">
          <ButtonOrange onClick={onDismiss}>{t('generics:ok')}</ButtonOrange>
        </div>
      </div>
    </div>,
    document.body,
  )
}
